"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.serviceSelector = exports.useService = void 0;
const react_1 = require("react");
// eslint-disable-next-line @typescript-eslint/ban-types
class Service {
    constructor() {
        /** Service context proxy object */
        this.contextProxy = {};
        /** Service listeners */
        this.updaters = new Set();
        this.getCurrentContext = () => {
            return Object.assign({}, this.contextProxy);
        };
        this.queueSequence = 0;
        this.queueCommand = () => {
            new Promise((resolve) => {
                const currentSequence = this.queueSequence;
                this.queueSequence = this.queueSequence + 1;
                resolve(currentSequence);
            }).then((current) => {
                // only execute once
                if (current === 0) {
                    this.dispatchUpdater();
                }
            });
        };
        this.dispatchUpdater = () => {
            this.updaters.forEach((updater) => {
                updater === null || updater === void 0 ? void 0 : updater(this.context);
            });
            // reset sequence
            this.queueSequence = 0;
        };
        this.subscribe = (updater) => {
            this.updaters.add(updater);
        };
        this.unsubscribe = (updater) => {
            this.updaters.delete(updater);
        };
    }
    /** Context getter, return context proxy object */
    get context() {
        return this.contextProxy;
    }
    /**
     * Context setter, store context values into context proxy object.
     * and set function will invoke dispatchHandlers function to trigger subscribe handlers.
     * */
    set context(value) {
        this.contextProxy = new Proxy(value, {
            set: (target, prop, value) => {
                try {
                    this.queueCommand();
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    target[prop] = value;
                }
                catch (_a) { }
                return true;
            }
        });
    }
}
exports.default = Service;
const isEqual = (prev, next) => {
    let prevValue = prev;
    let nextValue = next;
    if (prev instanceof Map) {
        prevValue = Object.fromEntries(prev);
    }
    if (next instanceof Map) {
        nextValue = Object.fromEntries(next);
    }
    if (prev instanceof Set) {
        prevValue = [...prev];
    }
    if (next instanceof Set) {
        nextValue = [...next];
    }
    return JSON.stringify(prevValue) === JSON.stringify(nextValue);
};
/**
 * Service hook, accept a service instance as param.
 * @param serviceInstance - instance create from Service
 */
const useService = (serviceInstance, selector) => {
    const _context = Object.assign({}, serviceInstance.context);
    const [selectedValue, setSelectedValue] = (0, react_1.useState)(selector(_context));
    (0, react_1.useEffect)(() => {
        const updater = (context) => {
            const nextSelectedValue = selector(Object.assign({}, context));
            if (!isEqual(selectedValue, nextSelectedValue)) {
                setSelectedValue(nextSelectedValue);
            }
        };
        serviceInstance.subscribe(updater);
        return () => serviceInstance.unsubscribe(updater);
    }, [selectedValue, selector, serviceInstance]);
    return selector(_context);
};
exports.useService = useService;
/**
 * Optimized Service Hook Selector Generator
 * @param subscriptionKeys - *Optional Optimization* pass in the keys for hook to subscribe for changes
 * @returns `selector` as required by `useService`
 */
const serviceSelector = (subscriptionKeys) => (context) => {
    if (subscriptionKeys === undefined) {
        return context;
    }
    return subscriptionKeys.reduce((accumulator, key) => (Object.assign(Object.assign({}, accumulator), { [`${key}`]: context[key] })), {});
};
exports.serviceSelector = serviceSelector;
